import { Box, Button, Grid, Stack } from '@mui/material';
import { IconPlus } from '@tabler/icons';
import { useModal } from 'mui-modal-provider';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { store } from 'store';
import { SET_CURRENT_STEP, SET_STEPS } from 'store/actions';
import { DefaultRootStateProps } from 'types';
import { v4 as uuid } from 'uuid';
import FormTitle from '../modals/FormTitle';
import Step from './Step';

const Steps = ({ currentStep, handleAdd, disabled, isEditing }) => {
  const form = useSelector((state: DefaultRootStateProps) => state.formBuilder);

  const intl = useIntl();
  const [draggedItem, setDraggedItem] = useState(null);
  const [openFormTitle, setOpenFormTitle] = useState(false);
  const { showModal } = useModal();

  const handleAddStep = useCallback(() => {
    const modal = showModal(FormTitle, {
      onConfirm: ({ title: titleToAdd, titleFrench: titleFrenchToAdd }) => {
        // Update steps using functional update to prevent potential issues
        console.log(titleToAdd, titleFrenchToAdd, 'title and title french');
        const title = titleToAdd ? titleToAdd : 'Title';
        const titleFrench = titleFrenchToAdd ? titleFrenchToAdd : 'Titre';
        const updatedSteps = form.steps;
        updatedSteps.push({
          title,
          titleFrench,
          items: []
        });

        store.dispatch({ type: SET_STEPS, payload: updatedSteps }); // Dispatch updated steps to Redux
        modal.hide();
        setOpenFormTitle(false);
      },
      onClose: () => {
        modal.hide();
        setOpenFormTitle(false);
      },
      open: openFormTitle,
      isAStep: true,
      stepIndex: form?.steps?.length ?? 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);
  const handleDragStart = (index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (index) => {
    const draggedOverItem = form?.steps[draggedItem ?? 0];
    if (index === draggedItem) return;

    const newSteps = form?.steps?.filter((_, idx) => idx !== draggedItem);
    newSteps.splice(index, 0, draggedOverItem);
    store.dispatch({ type: SET_STEPS, payload: newSteps });
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };
  const updateItemIds = (item) => {
    // Update the id of the current item
    const newItem = {
      ...item,
      id: uuid() // Generates a new unique id (UUID) for each item
    };

    // If the item has nested items, recursively update their id fields
    if (newItem.items && newItem.items.length > 0) {
      newItem.items = newItem.items.map((nestedItem) => updateItemIds(nestedItem));
    }

    return newItem;
  };
  const handleCopy = (index: number) => {
    const prevSteps = form.steps;
    const copiedStep = JSON.parse(JSON.stringify(prevSteps[index]));
    // Update _id and id fields for each item in the copied step
    copiedStep.items = copiedStep.items.map((item) => updateItemIds(item));
    const updatedSteps = [
      ...prevSteps.slice(0, index + 1), // Elements before the copied element
      // prevSteps[index], // Copied element
      copiedStep, // Copied element
      ...prevSteps.slice(index + 1)
    ];
    store.dispatch({ type: SET_STEPS, payload: updatedSteps });
  };

  const handleEdit = (index: number) => store.dispatch({ type: SET_CURRENT_STEP, payload: index });
  const handleDelete = (index: number) => {
    if (form.steps.length <= 1) {
      store.dispatch({ type: SET_STEPS, payload: [{ title: 'Title', titleFrench: 'Titre', items: [] }] });
      store.dispatch({ type: SET_CURRENT_STEP, payload: 0 });
    } else {
      const updatedSteps = form.steps.filter((_, stepIndex) => stepIndex !== index);
      store.dispatch({ type: SET_STEPS, payload: updatedSteps });
    }
  };

  return (
    <Grid container direction="column" sx={{ minHeight: '81vh', overflow: 'scroll' }}>
      <Grid item>
        {form.steps &&
          form.steps.map((step, index) => (
            <Box
              sx={{ border: currentStep === index ? '1px solid black' : 'none' }}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={() => handleDragOver(index)}
              onDragEnd={handleDragEnd}
              key={index}
            >
              <Step currentStep={step} index={index} handleCopy={handleCopy} handleDelete={handleDelete} handleEdit={handleEdit} />
            </Box>
          ))}
        <Stack direction="row" justifyContent="center" alignItems="center" onClick={handleAddStep}>
          <IconPlus color="#337AB7" />
          <Button>{intl.formatMessage({ defaultMessage: 'Add Step' })}</Button>
        </Stack>
      </Grid>
      <Grid item xs={4} p={2} sx={{ marginTop: 'auto' }}>
        <Button variant="contained" fullWidth onClick={handleAdd} disabled={disabled}>
          {isEditing ? intl.formatMessage({ defaultMessage: 'Update' }) : intl.formatMessage({ defaultMessage: 'Create' })}
        </Button>
      </Grid>
    </Grid>
  );
};

export default Steps;
